import { ArrowTopRightIcon, LightIcon } from "@xola/icons";
import { Alert, flash, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { GooglePreferences } from "types";
import { object, string } from "yup";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { Loader } from "../Loader";

const validationSchema = object().shape({
    gtmId: string().matches(/^GTM-[A-Z\d]{6,9}$|^$/, "should be valid GTM container ID GTM-XXXXXXX or empty"),
});

export const GoogleConversionTracking = () => {
    const seller = useSeller();
    const [preferences] = useState<GooglePreferences>({
        ...seller.preferences.google,
        gtmId: seller.preferences.google.gtmId ?? "",
    });
    const invalidate = useInvalidate();

    const { mutateAsync: update } = useMutation(async (payload: GooglePreferences) => {
        try {
            await api.post(`/users/${seller.id}/preferences/google`, payload);
            invalidate("sellers", seller.id);
            flash.success("Successfully updated");
        } catch {
            flash.danger("Error saving conversation tracking changes");
            return null;
        }
    });

    const onSave = async (values: GooglePreferences) => {
        await update(values);
    };

    if (!preferences) {
        return <Loader />;
    }

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={preferences}
            onSubmit={onSave}
        >
            {({ handleSubmit, handleReset, isSubmitting }) => {
                return (
                    <ContentLayout isWide isDisabled={isSubmitting}>
                        <ContentLayout.Section
                            title="Google Tag Manager"
                            description="Configure the GTM container to which Xola checkout pushes conversion data"
                        >
                            <FormField name="gtmId" label="Container ID">
                                <InputField placeholder="Container ID" />
                            </FormField>

                            <Alert color="primary">
                                <div className="flex p-2">
                                    <LightIcon />
                                    <div className="ml-2">
                                        <span className="text-base">
                                            Please ensure you have followed the steps listed in our help center article
                                            to configure event triggers. Without this additional step, conversion data
                                            may not be accurately sent to your tags.
                                        </span>
                                        <div className="mt-2 flex justify-end">
                                            <SecondaryButton
                                                size="small"
                                                icon={<ArrowTopRightIcon />}
                                                onClick={handleHelpCenterClick}
                                            >
                                                Visit Help Center
                                            </SecondaryButton>
                                        </div>
                                    </div>
                                </div>
                            </Alert>
                        </ContentLayout.Section>

                        <ContentLayout.Footer>
                            <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

const handleHelpCenterClick = () => {
    window.open("https://support.xola.com/google-analytics-4");
};
